import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { projectService } from '@/services';
import { useTheme } from '@/contexts/ThemeContext';
import { BryntumSchedulerPro } from '@bryntum/schedulerpro-react';
import '@bryntum/schedulerpro/fontawesome/css/fontawesome.css';
import '@bryntum/schedulerpro/fontawesome/css/solid.css';
import '@bryntum/schedulerpro/schedulerpro.css';
// Theme CSS loaded as URLs — swapped at runtime to follow light/dark toggle.
import stockholmLightHref from '@bryntum/schedulerpro/stockholm-light.css?url';
import stockholmDarkHref from '@bryntum/schedulerpro/stockholm-dark.css?url';

const API_URL = import.meta.env.VITE_API_URL as string;

export default function ProjectSchedule() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const schedulerRef = useRef<BryntumSchedulerPro | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string>('Schedule');

  // Swap Bryntum theme CSS via a <link> in <head> so the scheduler follows
  // the app's light/dark toggle. The link persists across route changes;
  // it's scoped to Bryntum's .b-* classes so it won't affect other pages.
  useEffect(() => {
    const href = theme === 'dark' ? stockholmDarkHref : stockholmLightHref;
    let link = document.querySelector<HTMLLinkElement>('link[data-bryntum-theme]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.setAttribute('data-bryntum-theme', '');
      document.head.appendChild(link);
    }
    if (link.href !== new URL(href, window.location.href).href) {
      link.href = href;
    }
  }, [theme]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      setToken(data.session?.access_token ?? null);
    })();
  }, []);

  useEffect(() => {
    if (!projectId) return;
    projectService.get(projectId).then(({ data }) => {
      if (data?.name) setProjectName(data.name);
    });
  }, [projectId]);

  const projectConfig = useMemo(() => {
    if (!projectId || !token) return null;

    const endpoint = `${API_URL}/api/scheduler/sync?projectId=${projectId}`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    return {
      autoLoad: true,
      autoSync: true,
      transport: {
        load: {
          url: endpoint,
          method: 'POST',
          headers,
        },
        sync: {
          url: endpoint,
          method: 'POST',
          headers,
        },
      },
    };
  }, [projectId, token]);

  const schedulerConfig = useMemo(() => ({
    columns: [
      { text: 'Name', field: 'name', width: 220 },
      { text: 'Start', field: 'startDate', type: 'date' as const, format: 'YYYY-MM-DD', width: 110 },
      { text: 'End', field: 'endDate', type: 'date' as const, format: 'YYYY-MM-DD', width: 110 },
      { text: 'Duration', field: 'duration', type: 'number' as const, width: 90 },
      { text: '% Done', field: 'percentDone', type: 'number' as const, width: 90 },
    ],
    features: {
      dependencies: true,
      dependencyEdit: true,
      eventEdit: true,
      eventMenu: true,
      taskEdit: true,
      timeRanges: true,
      resourceTimeRanges: true,
      nonWorkingTime: true,
    },
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
    endDate: new Date(new Date().setDate(new Date().getDate() + 60)),
    viewPreset: 'weekAndDayLetter' as const,
  }), []);

  if (!projectId) {
    return <div className="p-6">Missing project id.</div>;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-6rem)]">
      <div className="flex items-center gap-3 px-4 py-3 border-b">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/projects/${projectId}`)}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <h1 className="text-lg font-semibold">{projectName} — Schedule</h1>
      </div>
      <div className="flex-1 min-h-0">
        {projectConfig ? (
          <BryntumSchedulerPro
            ref={schedulerRef}
            project={projectConfig}
            {...schedulerConfig}
          />
        ) : (
          <div className="p-6 text-muted-foreground">Loading schedule…</div>
        )}
      </div>
    </div>
  );
}
